// OrderNow.tsx - Updated with discount display for group bookings
import React, { useState, useCallback, useEffect } from 'react';
import {
  User,
  Mail,
  Phone,
  Users,
  Globe,
  Calendar,
  Package,
  Send,
  X,
  CheckCircle,
  AlertCircle,
  Loader2,
  MessageSquare,
  ChevronDown,
  ShoppingCart,
  Plus,
  Minus,
  DollarSign,
  Receipt,
  FileText,
  UserPlus,
  Sun,
  Cloud,
  Snowflake,
  Calendar as CalendarIcon,
  Tag,
  Percent,
} from 'lucide-react';

// ============================================================
// TYPES
// ============================================================
export interface AddOn {
  label: string;
  value: string;
  url?: string;
  style?: 'primary' | 'secondary';
}

export interface PriceTier {
  label: string;
  value: string;
  minTravellers?: number;
  maxTravellers?: number;
  discount?: number; // Discount percentage
}

export interface SeasonalPricing {
  dates: [string, string];
  multiplier: number;
  label?: string;
}

export interface DateSpecificPricing {
  date: string;
  price: number;
  label?: string;
}

export interface Pricing {
  tiers?: {
    [key: string]: number;
  };
  seasonal?: {
    [key: string]: SeasonalPricing;
  };
  date_specific?: DateSpecificPricing[];
}

export interface PackageOption {
  value: string;
  label: string;
  price?: string;
  priceTiers?: PriceTier[];
  pricing?: Pricing;
}

export interface BookingFormData {
  fullName: string;
  email: string;
  phone: string;
  travellers: number;
  country: string;
  travellingDate: string;
  package: string;
  product: string;
  notes?: string;
  bookingType: 'package' | 'product' | 'service';
  selectedAddOns?: string[];
  addOnsDetails?: AddOn[];
  totalAddOnsPrice?: string;
  subtotal?: string;
  taxAmount?: string;
  serviceFee?: string;
  total?: string;
  priceTierApplied?: string;
  basePrice?: string;
  seasonalAdjustment?: string;
  dateSpecificPrice?: string;
  discountAmount?: string;
  discountPercentage?: string;
  originalPrice?: string;
}

export interface BookingResponse {
  success: boolean;
  message: string;
  bookingId?: string;
  error?: string;
}

export interface OrderNowProps {
  endpoint: string;
  method?: 'POST' | 'PUT' | 'PATCH';
  title?: string;
  subtitle?: string;
  submitLabel?: string;
  cancelLabel?: string;
  successMessage?: string;
  bookingType?: 'package' | 'product' | 'service';
  packageOptions?: PackageOption[];
  productOptions?: Array<{ value: string; label: string; price?: string }>;
  countryOptions?: Array<{ value: string; label: string }>;
  addOns?: AddOn[];
  showTravellers?: boolean;
  showTravellingDate?: boolean;
  showPackage?: boolean;
  showProduct?: boolean;
  showNotes?: boolean;
  showAddOns?: boolean;
  requireTravellers?: boolean;
  requireTravellingDate?: boolean;
  requirePackage?: boolean;
  requireProduct?: boolean;
  taxRate?: number;
  serviceFeeRate?: number;
  serviceFeeFixed?: number;
  currencySymbol?: string;
  showPriceBreakdown?: boolean;
  className?: string;
  buttonClassName?: string;
  inputClassName?: string;
  labelClassName?: string;
  modalClassName?: string;
  trigger?: 'button' | 'inline' | 'floating';
  triggerLabel?: string;
  triggerIcon?: React.ReactNode;
  autoOpen?: boolean;
  persistData?: boolean;
  onSuccess?: (data: BookingFormData, response: BookingResponse) => void;
  onError?: (error: Error) => void;
  onClose?: () => void;
}

// ============================================================
// DEFAULT COUNTRY OPTIONS
// ============================================================
const DEFAULT_COUNTRIES = [
  { value: 'US', label: 'United States' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'CA', label: 'Canada' },
  { value: 'AU', label: 'Australia' },
  { value: 'IN', label: 'India' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'SA', label: 'Saudi Arabia' },
  { value: 'MV', label: 'Maldives' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'SG', label: 'Singapore' },
  { value: 'LK', label: 'Sri Lanka' },
  { value: 'BD', label: 'Bangladesh' },
  { value: 'PK', label: 'Pakistan' },
  { value: 'EG', label: 'Egypt' },
  { value: 'TR', label: 'Turkey' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'IT', label: 'Italy' },
  { value: 'ES', label: 'Spain' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'SE', label: 'Sweden' },
  { value: 'NO', label: 'Norway' },
  { value: 'DK', label: 'Denmark' },
  { value: 'FI', label: 'Finland' },
  { value: 'JP', label: 'Japan' },
  { value: 'CN', label: 'China' },
  { value: 'KR', label: 'South Korea' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'PH', label: 'Philippines' },
  { value: 'TH', label: 'Thailand' },
  { value: 'VN', label: 'Vietnam' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'NG', label: 'Nigeria' },
  { value: 'KE', label: 'Kenya' },
  { value: 'BR', label: 'Brazil' },
  { value: 'AR', label: 'Argentina' },
  { value: 'MX', label: 'Mexico' },
  { value: 'RU', label: 'Russia' },
  { value: 'PL', label: 'Poland' },
  { value: 'PT', label: 'Portugal' },
  { value: 'GR', label: 'Greece' },
  { value: 'NZ', label: 'New Zealand' },
];

// ============================================================
// PRICE HELPERS
// ============================================================
const parsePrice = (priceStr: string): number => {
  if (!priceStr) return 0;
  let cleaned = priceStr.replace(/[^0-9.,]/g, '');
  if (cleaned.includes(',') && cleaned.includes('.')) {
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (cleaned.includes(',')) {
    const parts = cleaned.split(',');
    if (parts.length === 2 && parts[1].length <= 2) {
      cleaned = cleaned.replace(',', '.');
    } else {
      cleaned = cleaned.replace(/,/g, '');
    }
  }
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

const formatPrice = (amount: number, symbol: string = '$'): string => {
  return `${symbol}${amount.toFixed(2)}`;
};

// Check if a date falls within a range
const isDateInRange = (date: string, startDate: string, endDate: string): boolean => {
  const d = new Date(date);
  const start = new Date(startDate);
  const end = new Date(endDate);
  return d >= start && d <= end;
};

// Get seasonal pricing adjustment
const getSeasonalAdjustment = (date: string, pricing?: Pricing): { multiplier: number; label: string } => {
  if (!pricing?.seasonal) return { multiplier: 1, label: 'Standard' };
  
  for (const [key, season] of Object.entries(pricing.seasonal)) {
    if (isDateInRange(date, season.dates[0], season.dates[1])) {
      return {
        multiplier: season.multiplier,
        label: season.label || key.replace('_', ' ').toUpperCase(),
      };
    }
  }
  return { multiplier: 1, label: 'Standard' };
};

// Get date-specific pricing
const getDateSpecificPrice = (date: string, pricing?: Pricing): { price: number | null; label: string | null } => {
  if (!pricing?.date_specific) return { price: null, label: null };
  
  for (const item of pricing.date_specific) {
    if (item.date === date) {
      return { price: item.price, label: item.label || 'Special' };
    }
  }
  return { price: null, label: null };
};

// Calculate discount percentage between two prices
const calculateDiscountPercentage = (original: number, discounted: number): number => {
  if (original === 0) return 0;
  return Math.round(((original - discounted) / original) * 100);
};

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function OrderNow({
  endpoint,
  method = 'POST',
  title = 'Book Now',
  subtitle = 'Fill in your details to complete your booking',
  submitLabel = 'Submit Booking',
  cancelLabel = 'Cancel',
  successMessage = 'Your booking has been submitted successfully!',
  bookingType = 'package',
  packageOptions = [],
  productOptions = [],
  countryOptions = DEFAULT_COUNTRIES,
  addOns = [],
  showTravellers = true,
  showTravellingDate = true,
  showPackage = true,
  showProduct = false,
  showNotes = true,
  showAddOns = true,
  requireTravellers = false,
  requireTravellingDate = false,
  requirePackage = false,
  requireProduct = false,
  taxRate = 0.12,
  serviceFeeRate = 0.05,
  serviceFeeFixed = 0,
  currencySymbol = '$',
  showPriceBreakdown = true,
  className = '',
  buttonClassName = '',
  inputClassName = '',
  labelClassName = '',
  modalClassName = '',
  trigger = 'button',
  triggerLabel = 'Book Now',
  triggerIcon = <Calendar className="w-4 h-4" />,
  autoOpen = false,
  persistData = true,
  onSuccess,
  onError,
  onClose,
}: OrderNowProps) {
  const [isOpen, setIsOpen] = useState(autoOpen);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  const [currentPriceTier, setCurrentPriceTier] = useState<string>('');
  const [seasonalLabel, setSeasonalLabel] = useState<string>('Standard');
  const [dateSpecificLabel, setDateSpecificLabel] = useState<string | null>(null);
  const [discountInfo, setDiscountInfo] = useState<{ amount: number; percentage: number } | null>(null);
  
  const [formData, setFormData] = useState<BookingFormData>(() => {
    const saved = persistData ? localStorage.getItem('orderNow_data') : null;
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // ignore
      }
    }
    return {
      fullName: '',
      email: '',
      phone: '',
      travellers: 1,
      country: '',
      travellingDate: '',
      package: '',
      product: '',
      notes: '',
      bookingType,
      selectedAddOns: [],
      addOnsDetails: [],
      totalAddOnsPrice: formatPrice(0, currencySymbol),
      subtotal: formatPrice(0, currencySymbol),
      taxAmount: formatPrice(0, currencySymbol),
      serviceFee: formatPrice(0, currencySymbol),
      total: formatPrice(0, currencySymbol),
      priceTierApplied: '',
      basePrice: formatPrice(0, currencySymbol),
      seasonalAdjustment: 'Standard',
      dateSpecificPrice: '',
      discountAmount: formatPrice(0, currencySymbol),
      discountPercentage: '0%',
      originalPrice: formatPrice(0, currencySymbol),
    };
  });

  // Save form data to localStorage
  useEffect(() => {
    if (persistData) {
      localStorage.setItem('orderNow_data', JSON.stringify({
        ...formData,
        selectedAddOns,
      }));
    }
  }, [formData, selectedAddOns, persistData]);

  // Handle auto-open
  useEffect(() => {
    if (autoOpen) {
      setIsOpen(true);
    }
  }, [autoOpen]);

  // Calculate prices when dependencies change
  useEffect(() => {
    updatePriceCalculation();
  }, [selectedAddOns, formData.package, formData.travellers, formData.travellingDate, packageOptions]);

  // Handle form field changes
  const handleChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value,
    }));
    setError(null);
  }, []);

  // Handle add-on toggle
  const toggleAddOn = useCallback((addOnLabel: string) => {
    setSelectedAddOns(prev => {
      if (prev.includes(addOnLabel)) {
        return prev.filter(item => item !== addOnLabel);
      } else {
        return [...prev, addOnLabel];
      }
    });
  }, []);

  // Get selected add-ons details
  const getSelectedAddOnsDetails = useCallback(() => {
    return addOns.filter(addon => selectedAddOns.includes(addon.label));
  }, [addOns, selectedAddOns]);

  // Get price tier based on traveller count with discount calculation
  const getPriceTier = useCallback((travellers: number, pkg: PackageOption) => {
    if (!pkg.priceTiers || pkg.priceTiers.length === 0) {
      // Check if pricing object exists
      if (pkg.pricing?.tiers) {
        const tiers = pkg.pricing.tiers;
        const keys = Object.keys(tiers).map(Number).sort((a, b) => a - b);
        let selectedKey = keys[0];
        let originalKey = keys[0];
        for (const k of keys) {
          if (k <= travellers) {
            selectedKey = k;
          } else {
            break;
          }
        }
        const price = tiers[selectedKey];
        const originalPrice = tiers[originalKey];
        const discount = originalPrice > price ? originalPrice - price : 0;
        const discountPercentage = originalPrice > 0 ? (discount / originalPrice) * 100 : 0;
        
        if (discount > 0) {
          setDiscountInfo({
            amount: discount,
            percentage: Math.round(discountPercentage),
          });
        } else {
          setDiscountInfo(null);
        }

        return {
          tier: { 
            label: selectedKey === 1 ? 'Single' : `${selectedKey}+ Travellers`,
            value: formatPrice(price, '$'),
            discount: discountPercentage,
          },
          price: formatPrice(price, '$'),
          originalPrice: formatPrice(originalPrice, '$'),
        };
      }
      return { tier: null, price: '0', originalPrice: '0' };
    }

    // Find matching tier based on traveller count
    let selectedTier = null;
    let originalTier = null;
    
    for (const tier of pkg.priceTiers) {
      const min = tier.minTravellers || 0;
      const max = tier.maxTravellers || Infinity;
      if (travellers >= min && travellers <= max) {
        selectedTier = tier;
        break;
      }
    }
    
    // Get original tier (single traveller)
    for (const tier of pkg.priceTiers) {
      const min = tier.minTravellers || 0;
      if (min === 1 || tier.label.toLowerCase().includes('single')) {
        originalTier = tier;
        break;
      }
    }
    
    if (selectedTier && originalTier) {
      const selectedPrice = parsePrice(selectedTier.value);
      const originalPrice = parsePrice(originalTier.value);
      const discount = originalPrice > selectedPrice ? originalPrice - selectedPrice : 0;
      const discountPercentage = originalPrice > 0 ? (discount / originalPrice) * 100 : 0;
      
      if (discount > 0) {
        setDiscountInfo({
          amount: discount,
          percentage: Math.round(discountPercentage),
        });
      } else {
        setDiscountInfo(null);
      }
      
      return {
        tier: selectedTier,
        price: selectedTier.value,
        originalPrice: originalTier.value,
      };
    }

    return { tier: null, price: '0', originalPrice: '0' };
  }, []);

  // Get base price with seasonal adjustments and discounts
  const getBasePrice = useCallback(() => {
    const selectedPkg = packageOptions.find(p => p.value === formData.package);
    if (!selectedPkg) return { price: 0, originalPrice: 0, discount: 0 };

    // Get base price from tier
    const result = getPriceTier(formData.travellers || 1, selectedPkg);
    let basePrice = parsePrice(result.price);
    let originalPrice = parsePrice(result.originalPrice || result.price);
    let discount = originalPrice - basePrice;
    
    if (result.tier) {
      setCurrentPriceTier(result.tier.label);
    }

    // Check for date-specific pricing
    if (formData.travellingDate && selectedPkg.pricing) {
      const dateSpecific = getDateSpecificPrice(formData.travellingDate, selectedPkg.pricing);
      if (dateSpecific.price !== null) {
        setDateSpecificLabel(dateSpecific.label);
        // Date-specific price overrides everything
        originalPrice = dateSpecific.price;
        basePrice = dateSpecific.price;
        discount = 0;
        setDiscountInfo(null);
        return { price: basePrice, originalPrice, discount };
      }
      setDateSpecificLabel(null);

      // Check for seasonal adjustments
      const seasonal = getSeasonalAdjustment(formData.travellingDate, selectedPkg.pricing);
      if (seasonal.multiplier !== 1) {
        setSeasonalLabel(seasonal.label);
        const adjustedPrice = basePrice * seasonal.multiplier;
        const adjustedOriginalPrice = originalPrice * seasonal.multiplier;
        discount = adjustedOriginalPrice - adjustedPrice;
        if (discount > 0 && discountInfo) {
          setDiscountInfo({
            amount: discount,
            percentage: Math.round((discount / adjustedOriginalPrice) * 100),
          });
        }
        return { price: adjustedPrice, originalPrice: adjustedOriginalPrice, discount };
      }
    }
    
    setSeasonalLabel('Standard');
    return { price: basePrice, originalPrice, discount };
  }, [formData.package, formData.travellers, formData.travellingDate, packageOptions, getPriceTier, discountInfo]);

  // Calculate total add-ons price
  const getTotalAddOnsPrice = useCallback(() => {
    if (!addOns || addOns.length === 0) return 0;
    return addOns
      .filter(addon => selectedAddOns.includes(addon.label))
      .reduce((sum, addon) => {
        const price = parsePrice(addon.value);
        return sum + price;
      }, 0);
  }, [addOns, selectedAddOns]);

  // Update all price calculations
  const updatePriceCalculation = useCallback(() => {
    const { price: base, originalPrice, discount } = getBasePrice();
    const travellers = formData.travellers || 1;
    const addOnsTotal = getTotalAddOnsPrice();
    const totalBasePrice = base * travellers;
  const totalOriginalPrice = originalPrice * travellers;
  const totalAddOnsPrice = addOnsTotal * travellers;
  const subtotal = totalBasePrice + totalAddOnsPrice;
  const originalSubtotal = totalOriginalPrice + totalAddOnsPrice;
  const totalDiscount = originalSubtotal - subtotal;

    
    const taxAmount = taxRate > 0 ? subtotal * taxRate : 0;
    const serviceFee = serviceFeeFixed > 0 ? serviceFeeFixed : (serviceFeeRate > 0 ? subtotal * serviceFeeRate : 0);
    const total = subtotal + taxAmount + serviceFee;

    const discountPercentage = originalSubtotal > 0 ? Math.round((totalDiscount / originalSubtotal) * 100) : 0;

      setFormData(prev => ({
    ...prev,
    basePrice: formatPrice(base, currencySymbol),
    originalPrice: formatPrice(originalPrice, currencySymbol),
    discountAmount: formatPrice(totalDiscount, currencySymbol),
    discountPercentage: `${discountPercentage}%`,
    totalAddOnsPrice: formatPrice(totalAddOnsPrice, currencySymbol),
    subtotal: formatPrice(subtotal, currencySymbol),
    taxAmount: formatPrice(taxAmount, currencySymbol),
    serviceFee: formatPrice(serviceFee, currencySymbol),
    total: formatPrice(total, currencySymbol),
    priceTierApplied: currentPriceTier || 'Standard',
    seasonalAdjustment: seasonalLabel,
    dateSpecificPrice: dateSpecificLabel || '',
  }));
}, [getBasePrice, getTotalAddOnsPrice, taxRate, serviceFeeRate, serviceFeeFixed, currencySymbol, currentPriceTier, seasonalLabel, dateSpecificLabel, formData.travellers]);


  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    // Validate required fields
    if (!formData.fullName.trim()) {
      setError('Full name is required');
      setIsSubmitting(false);
      return;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Valid email address is required');
      setIsSubmitting(false);
      return;
    }
    if (!formData.phone.trim()) {
      setError('Phone number is required');
      setIsSubmitting(false);
      return;
    }
    if (!formData.country) {
      setError('Country is required');
      setIsSubmitting(false);
      return;
    }
    if (requireTravellers && (!formData.travellers || formData.travellers < 1)) {
      setError('Number of travellers is required');
      setIsSubmitting(false);
      return;
    }
    if (requireTravellingDate && !formData.travellingDate) {
      setError('Travelling date is required');
      setIsSubmitting(false);
      return;
    }
    if (requirePackage && !formData.package) {
      setError('Package selection is required');
      setIsSubmitting(false);
      return;
    }

    try {
    const selectedAddOnsDetails = getSelectedAddOnsDetails();
    const { price: base, originalPrice, discount } = getBasePrice();
    const travellers = formData.travellers || 1;
    const addOnsTotal = getTotalAddOnsPrice();
    
    // Calculate totals with travellers multiplier
    const totalBasePrice = base * travellers;
    const totalOriginalPrice = originalPrice * travellers;
    const totalAddOnsPrice = addOnsTotal * travellers;
    const subtotal = totalBasePrice + totalAddOnsPrice;
    const originalSubtotal = totalOriginalPrice + totalAddOnsPrice;
    const totalDiscount = originalSubtotal - subtotal;
    const taxAmount = taxRate > 0 ? subtotal * taxRate : 0;
    const serviceFee = serviceFeeFixed > 0 ? serviceFeeFixed : (serviceFeeRate > 0 ? subtotal * serviceFeeRate : 0);
    const total = subtotal + taxAmount + serviceFee;
    const discountPercentage = originalSubtotal > 0 ? Math.round((totalDiscount / originalSubtotal) * 100) : 0;

    const response = await fetch(endpoint, {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...formData,
        bookingType,
        selectedAddOns: selectedAddOns,
        addOnsDetails: selectedAddOnsDetails,
        priceTierApplied: currentPriceTier || 'Standard',
        seasonalAdjustment: seasonalLabel,
        dateSpecificPrice: dateSpecificLabel || '',
        travellers: formData.travellers,
        travellingDate: formData.travellingDate,
        // Send all price details
        pricing: {
          perTraveller: {
            base: base,
            original: originalPrice,
            discount: discount,
          },
          totals: {
            basePrice: totalBasePrice,
            originalPrice: totalOriginalPrice,
            addOns: totalAddOnsPrice,
            subtotal: subtotal,
            originalSubtotal: originalSubtotal,
            discount: totalDiscount,
            tax: taxAmount,
            serviceFee: serviceFee,
            total: total,
          },
          discountPercentage: discountPercentage,
        },
        priceBreakdown: {
          basePrice: formatPrice(totalBasePrice, currencySymbol),
          originalPrice: formatPrice(totalOriginalPrice, currencySymbol),
          addOnsTotal: formatPrice(totalAddOnsPrice, currencySymbol),
          subtotal: formatPrice(subtotal, currencySymbol),
          originalSubtotal: formatPrice(originalSubtotal, currencySymbol),
          discountAmount: formatPrice(totalDiscount, currencySymbol),
          discountPercentage: `${discountPercentage}%`,
          taxAmount: formatPrice(taxAmount, currencySymbol),
          serviceFee: formatPrice(serviceFee, currencySymbol),
          total: formatPrice(total, currencySymbol),
        },
        submittedAt: new Date().toISOString(),
      }),
    });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: BookingResponse = await response.json();

      if (result.success) {
        setIsSuccess(true);
        setBookingId(result.bookingId || null);
        if (onSuccess) {
          onSuccess({
            ...formData,
            selectedAddOns,
            addOnsDetails: selectedAddOnsDetails,
            priceTierApplied: currentPriceTier || 'Standard',
            seasonalAdjustment: seasonalLabel,
            dateSpecificPrice: dateSpecificLabel || '',
            discountAmount: formatPrice(totalDiscount, currencySymbol),
            discountPercentage: `${discountPercentage}%`,
            originalPrice: formatPrice(originalSubtotal, currencySymbol),
            totalAddOnsPrice: formatPrice(addOnsTotal, currencySymbol),
            basePrice: formatPrice(base, currencySymbol),
            subtotal: formatPrice(subtotal, currencySymbol),
            taxAmount: formatPrice(taxAmount, currencySymbol),
            serviceFee: formatPrice(serviceFee, currencySymbol),
            total: formatPrice(total, currencySymbol),
          }, result);
        }
        if (persistData) {
          localStorage.removeItem('orderNow_data');
        }
      } else {
        setError(result.message || 'Booking submission failed');
        if (onError) onError(new Error(result.message || 'Booking failed'));
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      if (onError) onError(err instanceof Error ? err : new Error(errorMessage));
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, endpoint, method, bookingType, requireTravellers, requireTravellingDate, requirePackage, requireProduct, onSuccess, onError, persistData, selectedAddOns, getSelectedAddOnsDetails, getTotalAddOnsPrice, getBasePrice, taxRate, serviceFeeRate, serviceFeeFixed, currencySymbol, currentPriceTier, seasonalLabel, dateSpecificLabel]);


  // Handle reset
  const handleReset = useCallback(() => {
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      travellers: 1,
      country: '',
      travellingDate: '',
      package: '',
      product: '',
      notes: '',
      bookingType,
      selectedAddOns: [],
      addOnsDetails: [],
      totalAddOnsPrice: formatPrice(0, currencySymbol),
      subtotal: formatPrice(0, currencySymbol),
      taxAmount: formatPrice(0, currencySymbol),
      serviceFee: formatPrice(0, currencySymbol),
      total: formatPrice(0, currencySymbol),
      priceTierApplied: '',
      basePrice: formatPrice(0, currencySymbol),
      seasonalAdjustment: 'Standard',
      dateSpecificPrice: '',
      discountAmount: formatPrice(0, currencySymbol),
      discountPercentage: '0%',
      originalPrice: formatPrice(0, currencySymbol),
    });
    setSelectedAddOns([]);
    setCurrentPriceTier('');
    setSeasonalLabel('Standard');
    setDateSpecificLabel(null);
    setDiscountInfo(null);
    setError(null);
    setIsSuccess(false);
    setBookingId(null);
    if (persistData) {
      localStorage.removeItem('orderNow_data');
    }
  }, [bookingType, persistData, currencySymbol]);

  // Handle close
  const handleClose = useCallback(() => {
    setIsOpen(false);
    if (onClose) onClose();
  }, [onClose]);

  // Handle open
  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setError(null);
    setIsSuccess(false);
  }, []);

  // If trigger is 'inline', render the form directly
  if (trigger === 'inline') {
    return (
      <div className={`w-full max-w-2xl mx-auto ${className}`}>
        {renderForm()}
      </div>
    );
  }

  // Default: button trigger
  return (
    <>
      <button
        onClick={handleOpen}
        className={`flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-lg transition-all duration-300 hover:scale-105 font-medium ${buttonClassName}`}
      >
        {triggerIcon}
        {triggerLabel}
      </button>
      {isOpen && renderModal()}
    </>
  );

  // ============================================================
  // RENDER HELPERS
  // ============================================================
  function renderModal() {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose();
        }}
      >
        <div className={`bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[95vh] overflow-auto relative ${modalClassName}`}>
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="p-6 md:p-8">
            {isSuccess ? renderSuccess() : renderForm()}
          </div>
        </div>
      </div>
    );
  }

  function renderAddOns() {
    if (!showAddOns || !addOns || addOns.length === 0) return null;

    const totalPrice = getTotalAddOnsPrice();
    const selectedCount = selectedAddOns.length;

    return (
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-purple-500" />
            <h4 className="font-semibold text-gray-800">Add-Ons</h4>
            {selectedCount > 0 && (
              <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">
                {selectedCount} selected
              </span>
            )}
          </div>
          {selectedCount > 0 && (
            <span className="text-sm font-semibold text-purple-600">
              Total: {formatPrice(totalPrice, currencySymbol)}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {addOns.map((addon, index) => {
            const isSelected = selectedAddOns.includes(addon.label);
            return (
              <div
                key={index}
                onClick={() => toggleAddOn(addon.label)}
                className={`cursor-pointer p-3 rounded-lg border-2 transition-all duration-200 ${
                  isSelected
                    ? 'border-purple-500 bg-purple-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-sm'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                      isSelected
                        ? 'border-purple-500 bg-purple-500'
                        : 'border-gray-300 bg-white'
                    }`}>
                      {isSelected && <CheckCircle className="text-white w-3 h-3" />}
                    </div>
                    <span className="text-sm font-medium text-gray-700">{addon.label}</span>
                  </div>
                  <span className={`text-sm font-semibold ${
                    addon.style === 'primary' ? 'text-blue-600' : 'text-gray-600'
                  }`}>
                    {addon.value}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function renderPriceBreakdown() {
  if (!showPriceBreakdown) return null;

  const base = parsePrice(formData.basePrice || '0');
  const originalPrice = parsePrice(formData.originalPrice || '0');
  const discountAmount = parsePrice(formData.discountAmount || '0');
  const addOnsTotal = parsePrice(formData.totalAddOnsPrice || '0');
  const travellers = formData.travellers || 1;
  
  // Calculate per-traveller and total amounts
  const perTravellerBase = base;
  const perTravellerOriginal = originalPrice || base;
  const totalBasePrice = base * travellers;
  const totalOriginalPrice = originalPrice * travellers;
  const totalAddOnsPrice = addOnsTotal * travellers;
  const subtotal = totalBasePrice + totalAddOnsPrice;
  const originalSubtotal = totalOriginalPrice + totalAddOnsPrice;
  const totalDiscountAmount = originalSubtotal - subtotal;
  
  const tax = taxRate > 0 ? subtotal * taxRate : 0;
  const serviceFee = serviceFeeFixed > 0 ? serviceFeeFixed : (serviceFeeRate > 0 ? subtotal * serviceFeeRate : 0);
  const total = subtotal + tax + serviceFee;

  const hasSeasonal = formData.seasonalAdjustment && formData.seasonalAdjustment !== 'Standard';
  const hasDateSpecific = formData.dateSpecificPrice && formData.dateSpecificPrice !== '';
  const hasDiscount = totalDiscountAmount > 0;
  const discountPercentage = originalSubtotal > 0 ? Math.round((totalDiscountAmount / originalSubtotal) * 100) : 0;

  if (subtotal === 0 && tax === 0 && serviceFee === 0 && total === 0) return null;

  return (
    <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 border border-gray-200">
      <div className="flex items-center gap-2 mb-3">
        <Receipt className="w-5 h-5 text-blue-500" />
        <h4 className="font-semibold text-gray-800">Price Breakdown</h4>
        {(hasSeasonal || hasDateSpecific || hasDiscount) && (
          <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full ml-auto">
            {hasDateSpecific ? formData.dateSpecificPrice : 
             hasSeasonal ? formData.seasonalAdjustment : 
             hasDiscount ? `${discountPercentage}% off` : ''}
          </span>
        )}
      </div>
      
      <div className="space-y-2 text-sm">
        {/* Travellers */}
        <div className="flex justify-between items-center pb-2 border-b border-gray-200">
          <span className="text-gray-600">Number of Travellers</span>
          <span className="font-medium text-gray-800">{travellers}</span>
        </div>

        {/* Original Price per traveller (if discounted) */}
        {hasDiscount && perTravellerOriginal > 0 && (
          <div className="flex justify-between items-center pb-2 border-b border-gray-200">
            <span className="text-gray-600">Original Price (per traveller)</span>
            <span className="text-gray-400 line-through">
              {formatPrice(perTravellerOriginal, currencySymbol)}
            </span>
          </div>
        )}

        {/* Discounted Price per traveller */}
        <div className="flex justify-between items-center pb-2 border-b border-gray-200">
          <span className="text-gray-600">
            Discounted Price (per traveller)
            {hasDiscount && (
              <span className="ml-2 inline-flex items-center gap-1 text-green-600 text-xs font-semibold">
                <Tag className="w-3 h-3" />
                {discountPercentage}% off
              </span>
            )}
          </span>
          <span className={`font-medium ${hasDiscount ? 'text-green-600' : 'text-gray-800'}`}>
            {formatPrice(perTravellerBase, currencySymbol)}
          </span>
        </div>

        {/* Base Price Total */}
        <div className="flex justify-between items-center pb-2 border-b border-gray-200">
          <span className="text-gray-600">Base Price ({travellers} travellers)</span>
          <span className="font-medium text-gray-800">
            {formatPrice(totalBasePrice, currencySymbol)}
          </span>
        </div>

        {/* Original Total (before discount) */}
        {hasDiscount && (
          <div className="flex justify-between items-center pb-2 border-b border-gray-200">
            <span className="text-gray-600">Total Before Discount</span>
            <span className="text-gray-500">
              {formatPrice(originalSubtotal, currencySymbol)}
            </span>
          </div>
        )}

        {/* Discount Amount */}
        {hasDiscount && (
          <div className="flex justify-between items-center pb-2 border-b border-green-200 bg-green-50/50 -mx-2 px-2 rounded-lg">
            <span className="text-green-600 flex items-center gap-1">
              <Tag className="w-3 h-3" />
              Total Savings
              <span className="text-xs text-green-500">({discountPercentage}% off)</span>
            </span>
            <span className="text-green-600 font-semibold">
              - {formatPrice(totalDiscountAmount, currencySymbol)}
            </span>
          </div>
        )}

        {/* Subtotal after discount */}
        <div className="flex justify-between items-center pb-2 border-b border-gray-200">
          <span className="text-gray-600">Subtotal After Discount</span>
          <span className="font-semibold text-gray-800">
            {formatPrice(subtotal, currencySymbol)}
          </span>
        </div>

        {/* Seasonal Adjustment */}
        {hasSeasonal && (
          <div className="flex justify-between items-center pb-2 border-b border-gray-200">
            <span className="text-gray-600 flex items-center gap-1">
              <Sun className="w-3 h-3 text-yellow-500" />
              {formData.seasonalAdjustment} Season
            </span>
            <span className="font-medium text-blue-600">
              {formData.seasonalAdjustment === 'Peak' ? '+' : ''}
              {base > 0 ? ((base - parsePrice(formData.basePrice || '0')) / parsePrice(formData.basePrice || '0') * 100).toFixed(0) : 0}%
            </span>
          </div>
        )}

        {/* Date Specific Price */}
        {hasDateSpecific && (
          <div className="flex justify-between items-center pb-2 border-b border-gray-200">
            <span className="text-gray-600 flex items-center gap-1">
              <CalendarIcon className="w-3 h-3 text-red-500" />
              {formData.dateSpecificPrice}
            </span>
            <span className="font-medium text-red-600">Special Rate</span>
          </div>
        )}

        {/* Add-ons */}
        {totalAddOnsPrice > 0 && (
          <div className="flex justify-between items-center pb-2 border-b border-gray-200">
            <span className="text-gray-600">Add-Ons ({travellers} travellers)</span>
            <span className="font-medium text-purple-600">
              + {formatPrice(totalAddOnsPrice, currencySymbol)}
            </span>
          </div>
        )}

        {/* Tax */}
        {tax > 0 && (
          <div className="flex justify-between items-center pb-2 border-b border-gray-200">
            <span className="text-gray-600">Tax ({Math.round(taxRate * 100)}%)</span>
            <span className="font-medium text-gray-800">
              + {formatPrice(tax, currencySymbol)}
            </span>
          </div>
        )}

        {/* Service Fee */}
        {serviceFee > 0 && (
          <div className="flex justify-between items-center pb-2 border-b border-gray-200">
            <span className="text-gray-600">
              Service Fee {serviceFeeFixed > 0 ? '(Fixed)' : `(${Math.round(serviceFeeRate * 100)}%)`}
            </span>
            <span className="font-medium text-gray-800">
              + {formatPrice(serviceFee, currencySymbol)}
            </span>
          </div>
        )}

        {/* Final Total */}
        <div className="flex justify-between items-center pt-2 border-t-2 border-gray-300">
          <span className="text-base font-bold text-gray-800">Final Total</span>
          <div className="text-right">
            {hasDiscount && (
              <div className="text-xs text-green-600 line-through text-gray-400">
                Was {formatPrice(originalSubtotal + tax + serviceFee, currencySymbol)}
              </div>
            )}
            <span className="text-xl font-bold text-blue-600">
              {formatPrice(total, currencySymbol)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

  function renderForm() {
    // Get current package for display
    const selectedPkg = packageOptions.find(p => p.value === formData.package);
    const hasPriceTiers = selectedPkg?.priceTiers && selectedPkg.priceTiers.length > 0;
    const hasDiscount = parsePrice(formData.discountAmount || '0') > 0;

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
          <p className="text-gray-500 text-sm mt-1">{subtitle}</p>
          {bookingId && (
            <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-sm">
              <CheckCircle className="w-4 h-4" />
              Booking ID: {bookingId}
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Price Alerts */}
        {formData.travellingDate && selectedPkg?.pricing && (
          <div className="space-y-2">
            {/* Seasonal Alert */}
            {formData.seasonalAdjustment && formData.seasonalAdjustment !== 'Standard' && (
              <div className={`flex items-start gap-3 p-3 rounded-xl border ${
                formData.seasonalAdjustment === 'Peak' 
                  ? 'bg-orange-50 border-orange-200 text-orange-700'
                  : 'bg-green-50 border-green-200 text-green-700'
              }`}>
                {formData.seasonalAdjustment === 'Peak' ? (
                  <Sun className="w-5 h-5 flex-shrink-0 mt-0.5" />
                ) : (
                  <Cloud className="w-5 h-5 flex-shrink-0 mt-0.5" />
                )}
                <p className="text-sm">
                  {formData.seasonalAdjustment} season pricing applied for {formData.travellingDate}
                </p>
              </div>
            )}

            {/* Date Specific Alert */}
            {formData.dateSpecificPrice && (
              <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700">
                <CalendarIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p className="text-sm">
                  Special pricing for {formData.dateSpecificPrice} on {formData.travellingDate}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Discount Alert */}
        {hasDiscount && (
          <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-xl text-green-700">
            <Tag className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold">🎉 Group Discount Applied!</p>
              <p className="text-green-600">
                You saved {formData.discountPercentage} on your booking 
                ({formData.discountAmount} off)
              </p>
            </div>
          </div>
        )}

        {/* Travellers Notice */}
        {hasPriceTiers && formData.travellers > 1 && (
          <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl text-blue-700">
            <UserPlus className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm">
              {formData.travellers} travellers - {formData.priceTierApplied || 'Group'} pricing applied
              {hasDiscount && ` (${formData.discountPercentage} discount)`}
            </p>
          </div>
        )}

        {/* Form Fields */}
        <div className="space-y-4">
          {/* Full Name */}
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-1 ${labelClassName}`}>
              Full Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="John Doe"
                className={`w-full pl-11 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition outline-none ${inputClassName}`}
                required
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-1 ${labelClassName}`}>
              Email Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@example.com"
                className={`w-full pl-11 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition outline-none ${inputClassName}`}
                required
              />
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-1 ${labelClassName}`}>
              Phone Number <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+1 234 567 8900"
                className={`w-full pl-11 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition outline-none ${inputClassName}`}
                required
              />
            </div>
          </div>

          {/* Travellers */}
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-1 ${labelClassName}`}>
              No. of Travellers <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="number"
                name="travellers"
                value={formData.travellers || ''}
                onChange={handleChange}
                min="1"
                max="100"
                placeholder="Number of travellers"
                className={`w-full pl-11 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition outline-none ${inputClassName}`}
                required
              />
            </div>
            {hasPriceTiers && selectedPkg?.priceTiers && (
              <div className="mt-1 text-xs text-gray-500">
                {selectedPkg.priceTiers.map((tier, idx) => (
                  <span key={idx} className="inline-block mr-3">
                    {tier.label}: {tier.value}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Country */}
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-1 ${labelClassName}`}>
              Country <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                className={`w-full pl-11 pr-10 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition outline-none appearance-none bg-white ${inputClassName}`}
                required
              >
                <option value="">Select your country</option>
                {countryOptions.map((country) => (
                  <option key={country.value} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Travelling Date */}
          {showTravellingDate && (
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${labelClassName}`}>
                Travelling Date {requireTravellingDate && <span className="text-red-500">*</span>}
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="date"
                  name="travellingDate"
                  value={formData.travellingDate}
                  onChange={handleChange}
                  className={`w-full pl-11 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition outline-none ${inputClassName}`}
                  required={requireTravellingDate}
                />
              </div>
            </div>
          )}

          {/* Package */}
          {showPackage && packageOptions.length > 0 && (
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${labelClassName}`}>
                Package {requirePackage && <span className="text-red-500">*</span>}
              </label>
              <div className="relative">
                <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  name="package"
                  value={formData.package}
                  onChange={handleChange}
                  className={`w-full pl-11 pr-10 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition outline-none appearance-none bg-white ${inputClassName}`}
                  required={requirePackage}
                >
                  <option value="">Select a package</option>
                  {packageOptions.map((pkg) => (
                    <option key={pkg.value} value={pkg.value}>
                      {pkg.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
            </div>
          )}

          {/* Notes */}
          {showNotes && (
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${labelClassName}`}>
                Additional Notes
              </label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <textarea
                  name="notes"
                  value={formData.notes || ''}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Any special requests or additional information..."
                  className={`w-full pl-11 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition outline-none resize-y ${inputClassName}`}
                />
              </div>
            </div>
          )}
        </div>

        {/* Add-Ons Section */}
        {renderAddOns()}

        {/* Price Breakdown */}
        {renderPriceBreakdown()}

        {/* Actions */}
        <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-100">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`flex-1 min-w-[120px] flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-lg transition-all duration-300 font-medium disabled:opacity-70 disabled:cursor-not-allowed ${buttonClassName}`}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                {submitLabel} - {formData.total}
              </>
            )}
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="px-6 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition font-medium"
          >
            Reset
          </button>
          {trigger !== 'inline' && (
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-3 text-gray-500 hover:text-gray-700 transition font-medium"
            >
              {cancelLabel}
            </button>
          )}
        </div>
      </form>
    );
  }

  function renderSuccess() {
    const selectedAddOnsDetails = getSelectedAddOnsDetails();
    const hasDiscount = parsePrice(formData.discountAmount || '0') > 0;

    return (
      <div className="text-center py-8">
        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h3>
        <p className="text-gray-600 mb-4">{successMessage}</p>
        
        {bookingId && (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm mb-4">
            Booking Reference: <strong>{bookingId}</strong>
          </div>
        )}

        {/* Show full price breakdown in success */}
        <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200 text-left">
          <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-500" />
            Booking Summary
          </p>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Package</span>
              <span className="font-medium text-gray-800">{formData.package || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Travellers</span>
              <span className="font-medium text-gray-800">{formData.travellers}</span>
            </div>
            {formData.priceTierApplied && (
              <div className="flex justify-between">
                <span className="text-gray-600">Price Tier</span>
                <span className="font-medium text-gray-800">{formData.priceTierApplied}</span>
              </div>
            )}
            
            {/* Show discount in success */}
            {hasDiscount && (
              <div className="flex justify-between bg-green-50 -mx-2 px-2 py-1 rounded-lg">
                <span className="text-green-600 flex items-center gap-1">
                  <Tag className="w-3 h-3" />
                  Discount Applied
                </span>
                <span className="text-green-600 font-semibold">
                  -{formData.discountAmount} ({formData.discountPercentage})
                </span>
              </div>
            )}

            {formData.seasonalAdjustment && formData.seasonalAdjustment !== 'Standard' && (
              <div className="flex justify-between">
                <span className="text-gray-600">Season</span>
                <span className="font-medium text-gray-800">{formData.seasonalAdjustment}</span>
              </div>
            )}
            {formData.dateSpecificPrice && (
              <div className="flex justify-between">
                <span className="text-gray-600">Special Pricing</span>
                <span className="font-medium text-red-600">{formData.dateSpecificPrice}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">Travelling Date</span>
              <span className="font-medium text-gray-800">{formData.travellingDate || 'N/A'}</span>
            </div>
            
            {selectedAddOnsDetails.length > 0 && (
              <>
                <div className="pt-2 border-t border-gray-200">
                  <p className="font-medium text-gray-700 mb-1">Add-Ons Selected:</p>
                  {selectedAddOnsDetails.map((addon, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-gray-600">{addon.label}</span>
                      <span className="font-medium text-gray-800">{addon.value}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="pt-2 border-t border-gray-200">
              {hasDiscount && (
                <div className="flex justify-between text-gray-400 line-through">
                  <span className="text-gray-400">Original Price</span>
                  <span className="text-gray-400">{formData.originalPrice}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Base Price</span>
                <span className="font-medium text-gray-800">{formData.basePrice}</span>
              </div>
              {parsePrice(formData.totalAddOnsPrice || '0') > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Add-Ons</span>
                  <span className="font-medium text-purple-600">{formData.totalAddOnsPrice}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium text-gray-800">{formData.subtotal}</span>
              </div>
              {parsePrice(formData.taxAmount || '0') > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax</span>
                  <span className="font-medium text-gray-800">{formData.taxAmount}</span>
                </div>
              )}
              {parsePrice(formData.serviceFee || '0') > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Service Fee</span>
                  <span className="font-medium text-gray-800">{formData.serviceFee}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-gray-300 font-bold">
                <span className="text-gray-800">Total Paid</span>
                <span className="text-blue-600">{formData.total}</span>
              </div>
            </div>
          </div>
        </div>

        <p className="text-sm text-gray-500 mt-4 mb-6">
          We will contact you shortly to confirm your booking.
        </p>
        <button
          onClick={handleReset}
          className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-medium"
        >
          Make Another Booking
        </button>
      </div>
    );
  }
}